import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import { getPayClinicList } from "../../services/newLeads";

export type Clinic = { id: number; name: string };

export interface NewLeadsContextType {
  clinics: Clinic[];
  selectedClinicIds: number[];
  startDate: string;
  endDate: string;
}

export const NewLeadsContext = createContext<NewLeadsContextType>({
  clinics: [],
  selectedClinicIds: [],
  startDate: "",
  endDate: "",
});

export const useNewLeadsContext = () => useContext(NewLeadsContext);

export const UrgencyBadge = ({ urgency }: { urgency: string }) => {
  if (urgency === "Emergency")
    return (
      <span
        style={{
          background: "#e53935",
          color: "#fff",
          borderRadius: 20,
          padding: "3px 12px",
          fontSize: 12,
          fontWeight: 600,
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          whiteSpace: "nowrap",
        }}
      >
        <i className="bi bi-exclamation-circle-fill" style={{ fontSize: 11 }} />{" "}
        Emergency
      </span>
    );
  if (urgency === "High")
    return (
      <span
        style={{
          border: "1.5px solid #f59e0b",
          color: "#b45309",
          borderRadius: 20,
          padding: "3px 12px",
          fontSize: 12,
          fontWeight: 600,
          background: "#fff8e1",
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          whiteSpace: "nowrap",
        }}
      >
        <i className="bi bi-clock" style={{ fontSize: 11 }} /> High
      </span>
    );
  return (
    <span
      style={{
        border: "1.5px solid #d1d5db",
        color: "#6b7280",
        borderRadius: 20,
        padding: "3px 12px",
        fontSize: 12,
        fontWeight: 500,
        background: "#f9fafb",
        whiteSpace: "nowrap",
      }}
    >
      Normal
    </span>
  );
};

export const StatusBadge = ({ status }: { status: string }) => {
  const styleMap: Record<string, React.CSSProperties> = {
    "Needs Action": { color: "#e53935", fontWeight: 600, fontSize: 13 },
    Converted: { color: "#16a34a", fontWeight: 600, fontSize: 13 },
  };
  return (
    <span style={styleMap[status] || { fontWeight: 600, fontSize: 13 }}>
      {status}
    </span>
  );
};

export const ResolutionBadge = ({ resolution }: { resolution: string }) => {
  const map: Record<string, React.CSSProperties> = {
    Partial: { background: "#e5e7eb", color: "#374151" },
    Denied: { background: "#fee2e2", color: "#b91c1c" },
    Closed: { background: "#e5e7eb", color: "#374151" },
    Approved: { background: "#d1fae5", color: "#065f46" },
  };
  const s = map[resolution] || { background: "#f3f4f6", color: "#374151" };
  return (
    <span
      style={{
        ...s,
        borderRadius: 12,
        padding: "3px 12px",
        fontSize: 12,
        fontWeight: 500,
        whiteSpace: "nowrap",
      }}
    >
      {resolution}
    </span>
  );
};

export const TypeCell = ({ type, icon }: { type: string; icon: string }) => {
  const iconMap: Record<string, string> = {
    globe2: "bi bi-globe2",
    telephone: "bi bi-telephone",
    gift: "bi bi-gift",
  };
  return (
    <span
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        color: "#6b7280",
        fontSize: 13,
      }}
    >
      <i className={iconMap[icon] || "bi bi-globe2"} style={{ fontSize: 13 }} />{" "}
      {type}
    </span>
  );
};

export const AssignToCell = () => {
  const { clinics } = useNewLeadsContext();
  const [selected, setSelected] = useState("");
  return (
    <div
      style={{ display: "flex", alignItems: "center", gap: 16 }}
      onClick={(e) => e.stopPropagation()}
    >
      <select
        value={selected}
        onChange={(e) => setSelected(e.target.value)}
        style={{
          border: "1px solid #d1d5db",
          borderRadius: 6,
          padding: "5px 10px",
          fontSize: 13,
          color: "#374151",
          background: "#fff",
          minWidth: 150,
        }}
      >
        <option value="">Select clinic...</option>
        {clinics.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>
      <button
        style={{
          color: "#194E5B",
          fontWeight: 600,
          cursor: "pointer",
          fontSize: 13,
        }}
      >
        Assign
      </button>
    </div>
  );
};

export const LoadingOverlay = () => (
  <div
    style={{
      position: "absolute",
      inset: 0,
      zIndex: 20,
      background: "rgba(255,255,255,0.75)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 12,
    }}
  >
    <div
      style={{
        width: 38,
        height: 38,
        border: "3.5px solid #e5e7eb",
        borderTopColor: "#16a09a",
        borderRadius: "50%",
        animation: "nlSpin 0.7s linear infinite",
      }}
    />
    <style>{`@keyframes nlSpin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

export const gridSx = {
  border: "none",
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#f9fafb",
    borderBottom: "1px solid #e5e7eb",
    fontSize: 12,
    fontWeight: 600,
    textTransform: "capitalize" as const,
    color: "#6b7280",
    letterSpacing: "0.06em",
    paddingLeft: "2px",
  },
  "& .MuiDataGrid-row:hover": {
    backgroundColor: "#f9fafb",
  },
  // "& .MuiDataGrid-columnSeparator": {
  //   display: "none",
  // },
  "& .MuiCheckbox-root.Mui-checked": {
    color: "#16a09a",
  },
  "& .MuiTablePagination-toolbar": {
    "& .MuiTablePagination-displayedRows": {
      marginBottom: 0,
    },
    "& .MuiTablePagination-selectLabel": {
      marginBottom: 0,
    },
  },
};

export const NewLeads = () => {
  const { pathname } = useLocation();
  const isUnassigned = pathname.includes("/unassigned");

  const [startDate, setStartDate] = useState(() =>
    moment().subtract(29, "day").format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState(() => moment().format("YYYY-MM-DD"));
  const [showDatePicker, setShowDatePicker] = useState(false);
  const datePickerRef = useRef<HTMLDivElement>(null);
  const [rangeType, setRangeType] = useState<
    "today" | "yesterday" | "7d" | "30d" | "custom"
  >("30d");

  const [clinics, setClinics] = useState<Clinic[]>([]);
  /** Single optional clinic filter; exposed to tabs as `selectedClinicIds: [] | [id]` */
  const [selectedClinicId, setSelectedClinicId] = useState<number | null>(null);
  const selectedClinicIds = selectedClinicId != null ? [selectedClinicId] : [];

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(e.target as Node)
      ) {
        setShowDatePicker(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    getPayClinicList()
      .then((res) => {
        const raw: any[] = res?.clinics || res?.data || [];
        const list: Clinic[] = raw.map((c: any) => ({
          id: c.id ?? c.uniqueId,
          name: c.name ?? c.clinicName ?? "",
        }));
        setClinics(list);
      })
      .catch(() => {});
  }, []);

  const displayLabel =
    startDate === endDate
      ? moment(startDate).format("MMM D")
      : `${moment(startDate).format("MMM D")} – ${moment(endDate).format(
          "MMM D"
        )}`;

  const quickRangeButtonStyle: React.CSSProperties = {
    borderRadius: 999,
    padding: "6px 14px",
    fontSize: 13,
    fontWeight: 500,
    border: "1px solid #e5e7eb",
    background: "#fff",
    color: "#374151",
    cursor: "pointer",
  };

  const quickRangeButtonActiveStyle: React.CSSProperties = {
    ...quickRangeButtonStyle,
    background: "#16a09a",
    borderColor: "#16a09a",
    color: "#fff",
    boxShadow: "0 0 0 1px #16a09a",
  };

  const setRange = (
    from: moment.Moment,
    to: moment.Moment,
    type: typeof rangeType
  ) => {
    setStartDate(from.format("YYYY-MM-DD"));
    setEndDate(to.format("YYYY-MM-DD"));
    setRangeType(type);
  };

  return (
    <NewLeadsContext.Provider
      value={{ clinics, selectedClinicIds, startDate, endDate }}
    >
      <section className="middle-navbar px-30">
        <div className="p-0 container-fluid">
          <div className="d-flex justify-content-between">
            <div className="w-100">
              <div className="d-flex flex-row justify-content-between w-100">
                <div className="nav-title-txt">Operations</div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  {/* Date range picker */}
                  <div ref={datePickerRef} style={{ position: "relative" }}>
                    <button
                      onClick={() => setShowDatePicker((v) => !v)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        border: "1px solid #d1d5db",
                        borderRadius: 999,
                        padding: "6px 16px",
                        background: showDatePicker ? "#f3f4f6" : "#fff",
                        cursor: "pointer",
                        fontSize: 13,
                        fontWeight: 500,
                        color: "#374151",
                      }}
                    >
                      <i className="bi bi-calendar3" style={{ fontSize: 13 }} />{" "}
                      {displayLabel}
                    </button>

                    {showDatePicker && (
                      <div
                        style={{
                          position: "absolute",
                          top: "calc(100% + 8px)",
                          right: 0,
                          zIndex: 1000,
                          background: "#fff",
                          border: "1px solid #e5e7eb",
                          borderRadius: 12,
                          boxShadow: "0 16px 40px rgba(15,23,42,0.18)",
                          padding: 16,
                          minWidth: 520,
                        }}
                      >
                        {/* Quick ranges row */}
                        <div
                          style={{
                            display: "flex",
                            gap: 8,
                            marginBottom: 16,
                            flexWrap: "wrap",
                          }}
                        >
                          <button
                            style={
                              rangeType === "today"
                                ? quickRangeButtonActiveStyle
                                : quickRangeButtonStyle
                            }
                            onClick={() =>
                              setRange(moment(), moment(), "today")
                            }
                          >
                            Today
                          </button>
                          <button
                            style={
                              rangeType === "yesterday"
                                ? quickRangeButtonActiveStyle
                                : quickRangeButtonStyle
                            }
                            onClick={() =>
                              setRange(
                                moment().subtract(1, "day"),
                                moment().subtract(1, "day"),
                                "yesterday"
                              )
                            }
                          >
                            Yesterday
                          </button>
                          <button
                            style={
                              rangeType === "7d"
                                ? quickRangeButtonActiveStyle
                                : quickRangeButtonStyle
                            }
                            onClick={() =>
                              setRange(
                                moment().subtract(6, "day"),
                                moment(),
                                "7d"
                              )
                            }
                          >
                            7 Days
                          </button>
                          <button
                            style={
                              rangeType === "30d"
                                ? quickRangeButtonActiveStyle
                                : quickRangeButtonStyle
                            }
                            onClick={() =>
                              setRange(
                                moment().subtract(29, "day"),
                                moment(),
                                "30d"
                              )
                            }
                          >
                            30 Days
                          </button>
                          <button
                            style={
                              rangeType === "custom"
                                ? quickRangeButtonActiveStyle
                                : quickRangeButtonStyle
                            }
                            onClick={() => {
                              setRangeType("custom");
                              setEndDate("");
                            }}
                          >
                            Custom
                          </button>
                        </div>

                        {/* Two-month calendar (Custom only) */}
                        {rangeType === "custom" && (
                          <ReactDatePicker
                            selectsRange
                            startDate={startDate ? moment(startDate).toDate() : null}
                            endDate={endDate ? moment(endDate).toDate() : null}
                            onChange={(dates) => {
                              const [start, end] = dates as [
                                Date | null,
                                Date | null
                              ];
                              if (start && !end) {
                                setStartDate(moment(start).format("YYYY-MM-DD"));
                                setEndDate("");
                                return;
                              }
                              if (end) {
                                if (start) {
                                  setStartDate(
                                    moment(start).format("YYYY-MM-DD")
                                  );
                                }
                                setEndDate(moment(end).format("YYYY-MM-DD"));
                                setRangeType("custom");
                                setShowDatePicker(false);
                              }
                            }}
                            inline
                            monthsShown={2}
                          />
                        )}
                      </div>
                    )}
                  </div>

                  {!isUnassigned && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        position: "relative",
                        minWidth: 0,
                      }}
                    >
                      <i
                        className="bi bi-funnel"
                        style={{
                          fontSize: 14,
                          color: "#6b7280",
                          flexShrink: 0,
                        }}
                        aria-hidden
                      />
                      <div
                        style={{
                          position: "relative",
                          minWidth: 200,
                          maxWidth: 320,
                        }}
                      >
                        <select
                          value={selectedClinicId ?? ""}
                          onChange={(e) => {
                            const v = e.target.value;
                            setSelectedClinicId(v === "" ? null : Number(v));
                          }}
                          aria-label="Filter by clinic"
                          style={{
                            width: "100%",
                            appearance: "none",
                            WebkitAppearance: "none",
                            MozAppearance: "none",
                            border: "1px solid #d1d5db",
                            borderRadius: 999,
                            padding: "7px 36px 7px 14px",
                            fontSize: 13,
                            fontWeight: 500,
                            color: "#374151",
                            background: "#fff",
                            cursor: "pointer",
                            outline: "none",
                            boxShadow: "none",
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = "#16a09a";
                            e.target.style.boxShadow =
                              "0 0 0 2px rgba(22,160,154,0.2)";
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = "#d1d5db";
                            e.target.style.boxShadow = "none";
                          }}
                        >
                          <option value="">All clinics</option>
                          {clinics.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                        <i
                          className="bi bi-chevron-down"
                          style={{
                            position: "absolute",
                            right: 12,
                            top: "50%",
                            transform: "translateY(-50%)",
                            pointerEvents: "none",
                            fontSize: 11,
                            color: "#9ca3af",
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <ul className="nav nav-tabs">
                <li className="nav-item">
                  <NavLink className="nav-link" to="/new-leads/unassigned">
                    Unassigned
                  </NavLink>
                </li>
                <li className="nav-item">
                  <NavLink className="nav-link" to="/new-leads/active">
                    Active
                  </NavLink>
                </li>
                <li className="nav-item">
                  <NavLink className="nav-link" to="/new-leads/completed">
                    Completed
                  </NavLink>
                </li>
                <li className="nav-item">
                  <NavLink className="nav-link" to="/new-leads/disputed">
                    Disputed
                  </NavLink>
                </li>
                <li className="nav-item">
                  <NavLink className="nav-link" to="/new-leads/assigned">
                    Assigned to Regular Clinic
                  </NavLink>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
      <Outlet />
    </NewLeadsContext.Provider>
  );
};
